import { useState } from 'react';
import { z } from 'zod';
import type { DocumentDto, PhaseId, UpdateDocumentCommand } from '@/api/generated';
import { useUpdateDocument, useUploadDocument } from '@/api/hooks';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import { toast } from '@/design-system/primitives/Toast/Toast';
import { useFileUpload } from '@/lib/useFileUpload';
import styles from './DocumentDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const MAX_BYTES = 25 * 1024 * 1024;

// Parse a comma-separated tag string into a deduped, trimmed, lowercased
// array. Empty input → []. The backend re-normalises (see
// UpdateDocumentHandler.cs) so this is purely defensive on the wire.
function parseTags(raw: string): string[] {
  const seen = new Set<string>();
  for (const piece of raw.split(',')) {
    const t = piece.trim().toLowerCase();
    if (t.length > 0) seen.add(t);
  }
  return Array.from(seen);
}

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; current: number; total: number }
  | { kind: 'done'; uploaded: number; failed: number };

export function DocumentUploadDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const upload = useUploadDocument();
  const picker = useFileUpload({ multiple: true, maxBytes: MAX_BYTES });
  const [phaseId, setPhaseId] = useState<PhaseId>(defaultPhaseId ?? 'Ruw');
  const [tagsInput, setTagsInput] = useState('');
  const [state, setState] = useState<UploadState>({ kind: 'idle' });

  const close = (next: boolean) => {
    if (!next) {
      picker.reset();
      setTagsInput('');
      setState({ kind: 'idle' });
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (picker.files.length === 0) return;
    setState({ kind: 'uploading', current: 0, total: picker.files.length });
    const tags = parseTags(tagsInput);
    let failed = 0;
    for (let i = 0; i < picker.files.length; i++) {
      const file = picker.files[i];
      try {
        // mutateAsync surfaces errors via the hook's onError toast; we count
        // the failures here so the dialog summary stays honest.
        await upload.mutateAsync({ file, phaseId, ...(tags.length > 0 ? { tags } : {}) });
      } catch {
        failed += 1;
      }
      setState({ kind: 'uploading', current: i + 1, total: picker.files.length });
    }
    const uploaded = picker.files.length - failed;
    setState({ kind: 'done', uploaded, failed });
    if (failed === 0) {
      toast.success(`${uploaded} bestand${uploaded === 1 ? '' : 'en'} geüpload.`);
      close(false);
    }
  };

  const isBusy = state.kind === 'uploading';

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogTitle>Documenten uploaden</DialogTitle>
        <DialogDescription>
          Sleep bestanden hierheen of kies ze handmatig. Tot {Math.round(MAX_BYTES / 1024 / 1024)}{' '}
          MB per bestand.
        </DialogDescription>
        <DialogBody>
          <input {...picker.inputProps} />
          <button
            type="button"
            {...picker.dropZoneProps}
            onClick={picker.openPicker}
            className={`${styles.dropZone} ${picker.isDragging ? styles.dropZoneActive : ''}`}
            disabled={isBusy}
          >
            <Icon name="upload" size={18} />{' '}
            <span>
              {picker.files.length === 0
                ? 'Klik om te selecteren of sleep bestanden hier'
                : `${picker.files.length} bestand${picker.files.length === 1 ? '' : 'en'} klaar`}
            </span>
          </button>
          {picker.error && (
            <div role="alert" className={styles.error}>
              {picker.error}
            </div>
          )}
          {picker.files.length > 0 && (
            <ul className={styles.fileList}>
              {picker.files.map((f) => (
                <li key={`${f.name}-${f.size}`} className="mono">
                  {f.name} — {Math.round(f.size / 1024)} KB
                </li>
              ))}
            </ul>
          )}

          <label className={styles.fieldLabel} htmlFor="upload-phase">
            Fase
          </label>
          <select
            id="upload-phase"
            value={phaseId}
            onChange={(e) => setPhaseId(e.target.value as PhaseId)}
            className={styles.select}
            disabled={isBusy}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>

          <label className={styles.fieldLabel} htmlFor="upload-tags">
            Tags
          </label>
          <Input
            id="upload-tags"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="bv. urgent, getekend, btw-6%"
            disabled={isBusy}
          />

          {state.kind === 'uploading' && (
            <div className={`mono ${styles.progress}`} aria-live="polite">
              Bezig… {state.current} / {state.total}
            </div>
          )}
          {state.kind === 'done' && state.failed > 0 && (
            <div role="alert" className={styles.error}>
              {state.failed} bestand{state.failed === 1 ? '' : 'en'} mislukt; bekijk de meldingen.
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button" disabled={isBusy}>
              Annuleer
            </Btn>
          </DialogClose>
          <Btn
            variant="primary"
            onClick={handleSubmit}
            disabled={isBusy || picker.files.length === 0}
          >
            {isBusy ? 'Bezig…' : 'Uploaden'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const editSchema = z.object({
  name: z.string().trim().min(2, 'Minstens 2 tekens'),
  phaseId: z.string().min(1, 'Kies een fase'),
  version: z.coerce.number().int().min(1, 'Versie ≥ 1'),
  tags: z.string().optional().default(''),
});

type EditValues = z.infer<typeof editSchema>;

export function DocumentEditDialog({
  open,
  onOpenChange,
  doc,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  doc: DocumentDto;
}) {
  const updateMutation = useUpdateDocument(doc.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Document bewerken</DialogTitle>
        <DialogDescription>Pas naam, fase, versie of tags van {doc.name} aan.</DialogDescription>
        <DialogBody>
          <Form<EditValues>
            schema={editSchema}
            defaultValues={{
              name: doc.name,
              phaseId: doc.phaseId,
              version: Number(doc.version) || 1,
              tags: (doc.tags ?? []).join(', '),
            }}
            onSubmit={async (values) => {
              const body: UpdateDocumentCommand = {
                name: values.name.trim(),
                phaseId: values.phaseId,
                version: values.version,
                tags: parseTags(values.tags ?? ''),
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FormField<EditValues> name="name" label="Naam" required>
              {(field) => <Input {...field} autoFocus />}
            </FormField>
            <FormField<EditValues> name="phaseId" label="Fase" required>
              {(field) => (
                <select
                  {...field}
                  className={styles.select}
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {PHASE_OPTIONS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
            <FormField<EditValues> name="version" label="Versie" required>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  value={
                    field.value === undefined || field.value === null ? '' : String(field.value)
                  }
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            </FormField>
            <FormField<EditValues> name="tags" label="Tags">
              {(field) => (
                <Input
                  {...field}
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="bv. urgent, getekend, btw-6%"
                />
              )}
            </FormField>
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
